import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { 
  Play, 
  Pause, 
  Square, 
  Clock, 
  Calendar,
  User,
  MapPin,
  Save,
  FileText
} from 'lucide-react';
import { format } from 'date-fns';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface MeetingTimerProps {
  meeting: any;
  onMeetingComplete: () => void;
}

export function MeetingTimer({ meeting, onMeetingComplete }: MeetingTimerProps) {
  const [isRunning, setIsRunning] = useState(false);
  const [startTime, setStartTime] = useState<Date | null>(null);
  const [endTime, setEndTime] = useState<Date | null>(null);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let interval: NodeJS.Timeout;
    
    if (isRunning && startTime) {
      interval = setInterval(() => {
        setElapsedTime(Math.floor((Date.now() - startTime.getTime()) / 1000));
      }, 1000);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isRunning, startTime]);

  useEffect(() => {
    if (meeting) {
      // Check if meeting is already in progress
      if (meeting.actual_start_time && !meeting.actual_end_time) {
        setStartTime(new Date(meeting.actual_start_time));
        setIsRunning(true);
        setElapsedTime(Math.floor((Date.now() - new Date(meeting.actual_start_time).getTime()) / 1000));
      }
      setNotes(meeting.notes || '');
    }
  }, [meeting]);

  const handleStart = async () => {
    const now = new Date();
    setStartTime(now);
    setIsRunning(true);
    
    try {
      await supabase
        .from('meetings')
        .update({
          actual_start_time: now.toISOString(),
          status: 'in_progress'
        })
        .eq('id', meeting.id);
      
      toast.success('Meeting timer started');
    } catch (error) {
      console.error('Error starting meeting:', error);
      toast.error('Failed to start meeting timer');
    }
  };

  const handlePause = () => {
    setIsRunning(false);
  };

  const handleResume = () => {
    setIsRunning(true);
  };

  const handleStop = async () => {
    const now = new Date();
    setEndTime(now);
    setIsRunning(false);

    try {
      await supabase
        .from('meetings')
        .update({
          actual_end_time: now.toISOString(),
          status: 'completed',
          notes: notes
        })
        .eq('id', meeting.id);
      
      toast.success('Meeting completed successfully');
      onMeetingComplete();
    } catch (error) {
      console.error('Error completing meeting:', error);
      toast.error('Failed to complete meeting');
    }
  };

  const handleSaveNotes = async () => {
    setSaving(true);
    try {
      await supabase
        .from('meetings')
        .update({ notes })
        .eq('id', meeting.id);
      
      toast.success('Notes saved successfully');
    } catch (error) {
      console.error('Error saving notes:', error);
      toast.error('Failed to save notes');
    } finally {
      setSaving(false);
    }
  };

  const formatTime = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hours > 0) {
      return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const getMeetingTypeColor = (type: string) => {
    switch (type) {
      case 'observation': return 'bg-purple-100 text-purple-800';
      case 'mentoring': return 'bg-green-100 text-green-800';
      case 'coaching': return 'bg-blue-100 text-blue-800';
      case 'evaluation': return 'bg-orange-100 text-orange-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (!meeting) {
    return (
      <Card>
        <CardContent className="py-8 text-center">
          <Clock className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-semibold">No Active Meeting</h3>
          <p className="text-muted-foreground">
            Select a meeting from the meetings tab to start timing.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Meeting Info Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-xl">{meeting.title}</CardTitle>
            <Badge className={getMeetingTypeColor(meeting.meeting_type)}>
              {meeting.meeting_type}
            </Badge>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-muted-foreground" />
              <span>{format(new Date(meeting.scheduled_date), 'PPP')}</span>
            </div>
            <div className="flex items-center gap-2">
              <User className="h-4 w-4 text-muted-foreground" />
              <span>{meeting.observee_profile?.first_name} {meeting.observee_profile?.last_name}</span>
            </div>
            {meeting.location && (
              <div className="flex items-center gap-2">
                <MapPin className="h-4 w-4 text-muted-foreground" />
                <span>{meeting.location}</span>
              </div>
            )}
          </div>
          {meeting.description && (
            <p className="text-muted-foreground mt-3">{meeting.description}</p>
          )}
        </CardContent>
      </Card>

      {/* Timer Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5" />
            Meeting Timer
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="text-center">
            <div className="text-6xl font-mono font-bold mb-4">
              {formatTime(elapsedTime)}
            </div>
            
            <div className="flex justify-center gap-2">
              {!startTime ? (
                <Button onClick={handleStart} size="lg" className="flex items-center gap-2">
                  <Play className="h-5 w-5" />
                  Start Meeting
                </Button>
              ) : (
                <>
                  {isRunning ? (
                    <Button onClick={handlePause} variant="outline" size="lg" className="flex items-center gap-2">
                      <Pause className="h-5 w-5" />
                      Pause
                    </Button>
                  ) : (
                    <Button onClick={handleResume} size="lg" className="flex items-center gap-2">
                      <Play className="h-5 w-5" />
                      Resume
                    </Button>
                  )}
                  
                  <Button 
                    onClick={handleStop} 
                    variant="destructive" 
                    size="lg" 
                    className="flex items-center gap-2"
                    disabled={!startTime}
                  >
                    <Square className="h-5 w-5" />
                    End Meeting
                  </Button>
                </>
              )}
            </div>
          </div>

          {startTime && (
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <Label className="text-muted-foreground">Started At</Label>
                <div className="font-mono">{format(startTime, 'HH:mm:ss')}</div>
              </div>
              {endTime && (
                <div>
                  <Label className="text-muted-foreground">Ended At</Label>
                  <div className="font-mono">{format(endTime, 'HH:mm:ss')}</div>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Notes Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Meeting Notes
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <Textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Add your observations, feedback, and notes here..."
            rows={8}
          />
          
          <div className="flex justify-end">
            <Button 
              onClick={handleSaveNotes} 
              disabled={saving}
              className="flex items-center gap-2"
            >
              <Save className="h-4 w-4" />
              {saving ? 'Saving...' : 'Save Notes'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}